import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import type {RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {normalizeError} from "@/utils";

type KpiMetric = {
    value: number;
    trend: string;
    positive: boolean;
};

type UrgentItem = {
    title: string;
    detail: string;
    severity: "critical" | "warning";
};

type TopPerformer = {
    name: string;
    channel: "Auction" | "Listing";
    sellThrough: number;
    margin: number;
};

export interface DashboardMetrics {
    activeListings: KpiMetric;
    liveAuctions: KpiMetric;
    todayRevenue: KpiMetric;
    avgSellTime: KpiMetric;
    auctionRevenue: number;
    listingRevenue: number;
    conversionRate: number;
    urgentQueue: UrgentItem[];
    topPerformers: TopPerformer[];
    hourlyBidHeat: number[];
}

interface DashboardState {
    metrics: DashboardMetrics;
    loading: boolean;
    error?: string;
}

const emptyMetrics: DashboardMetrics = {
    activeListings: {value: 0, trend: "0.0%", positive: true},
    liveAuctions: {value: 0, trend: "0.0%", positive: true},
    todayRevenue: {value: 0, trend: "0.0%", positive: true},
    avgSellTime: {value: 0, trend: "0.0d", positive: true},
    auctionRevenue: 0,
    listingRevenue: 0,
    conversionRate: 0,
    urgentQueue: [],
    topPerformers: [],
    hourlyBidHeat: Array.from({length: 12}, () => 0)
};

const initialState: DashboardState = {
    metrics: emptyMetrics,
    loading: false
};

function percentageTrend(current: number, previous: number) {
    if (previous === 0) {
        return {
            trend: current === 0 ? "0.0%" : "+100.0%",
            positive: true
        };
    }

    const change = ((current - previous) / previous) * 100;
    return {
        trend: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
        positive: change >= 0
    };
}

function dayTrend(current: number, previous: number) {
    const change = current - previous;
    return {
        trend: `${change >= 0 ? "+" : ""}${change.toFixed(1)}d`,
        positive: change <= 0
    };
}

function startOfDay(date = new Date()) {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
}

function startOfWindow(daysAgo: number) {
    const result = startOfDay();
    result.setDate(result.getDate() - daysAgo);
    return result;
}

function average(values: number[]) {
    if (values.length === 0) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function vehicleTitle(vehicle?: {name?: string; brand?: string; model?: string}) {
    return vehicle?.name || [vehicle?.brand, vehicle?.model].filter(Boolean).join(" ") || "Untitled vehicle";
}

function normalizeVehicleRelation<T>(vehicle: T | T[] | null | undefined): T | undefined {
    if (Array.isArray(vehicle)) {
        return vehicle[0];
    }

    return vehicle ?? undefined;
}

export const fetchDashboardMetrics = createAsyncThunk<
    DashboardMetrics,
    void,
    {state: RootState; rejectValue: string}
>("dashboard/fetchMetrics", async (_, {getState, rejectWithValue}) => {
    try {
        const dealerId = getState().authentication.user?.id;

        if (!dealerId) {
            return emptyMetrics;
        }

        const todayStartIso = startOfDay().toISOString();
        const currentWindowIso = startOfWindow(7).toISOString();
        const previousWindowIso = startOfWindow(14).toISOString();

        const [
            listingsResponse,
            auctionsResponse,
            bidsResponse,
            vehiclesResponse,
            eventsResponse
        ] = await Promise.all([
            supabase
                .from("listings")
                .select("id, price, status, created_at, updated_at, vehicle_id, vehicle:vehicles!inner(id, seller_id, name, brand, model, views, created_at)")
                .eq("vehicle.seller_id", dealerId),
            supabase
                .from("auctions")
                .select("id, seller_id, current_bid, starting_bid, ending, status, created_at, updated_at, vehicle_id, vehicle:vehicles!inner(id, seller_id, name, brand, model, views, created_at)")
                .eq("seller_id", dealerId),
            supabase
                .from("auction_bids")
                .select("id, amount, created_at, auction:auctions!inner(id, seller_id)")
                .eq("auction.seller_id", dealerId),
            supabase
                .from("vehicles")
                .select("id, seller_id, created_at, views, active, published, name, brand, model")
                .eq("seller_id", dealerId),
            supabase
                .from("vehicle_events")
                .select("vehicle_id, event_type")
                .eq("dealer_id", dealerId)
        ]);

        if (listingsResponse.error) return rejectWithValue(listingsResponse.error.message);
        if (auctionsResponse.error) return rejectWithValue(auctionsResponse.error.message);
        if (bidsResponse.error) return rejectWithValue(bidsResponse.error.message);
        if (vehiclesResponse.error) return rejectWithValue(vehiclesResponse.error.message);
        if (eventsResponse.error) return rejectWithValue(eventsResponse.error.message);

        const listings = listingsResponse.data ?? [];
        const auctions = auctionsResponse.data ?? [];
        const bids = bidsResponse.data ?? [];
        const vehicles = vehiclesResponse.data ?? [];
        const events = eventsResponse.data ?? [];
        const normalizedListings = listings.map((item) => ({
            ...item,
            vehicle: normalizeVehicleRelation(item.vehicle)
        }));
        const normalizedAuctions = auctions.map((item) => ({
            ...item,
            vehicle: normalizeVehicleRelation(item.vehicle)
        }));

        const activeListings = normalizedListings.filter((item) => item.status === "active").length;
        const previousActiveListings = normalizedListings.filter((item) => {
            const createdAt = new Date(item.created_at);
            return item.status === "active" && createdAt >= new Date(previousWindowIso) && createdAt < new Date(currentWindowIso);
        }).length;

        const liveAuctions = normalizedAuctions.filter((item) => item.status === "live").length;
        const previousLiveAuctions = normalizedAuctions.filter((item) => {
            const createdAt = new Date(item.created_at);
            return item.status === "live" && createdAt >= new Date(previousWindowIso) && createdAt < new Date(currentWindowIso);
        }).length;

        const soldListings = normalizedListings.filter((item) => item.status === "sold");
        const endedAuctions = normalizedAuctions.filter((item) => item.status === "ended");

        const todayListingRevenue = soldListings
            .filter((item) => new Date(item.updated_at) >= new Date(todayStartIso))
            .reduce((sum, item) => sum + Number(item.price ?? 0), 0);

        const todayAuctionRevenue = endedAuctions
            .filter((item) => new Date(item.updated_at) >= new Date(todayStartIso))
            .reduce((sum, item) => sum + Number(item.current_bid ?? 0), 0);

        const currentRevenue = todayListingRevenue + todayAuctionRevenue;

        const previousListingRevenue = soldListings
            .filter((item) => {
                const updatedAt = new Date(item.updated_at);
                return updatedAt >= new Date(previousWindowIso) && updatedAt < new Date(currentWindowIso);
            })
            .reduce((sum, item) => sum + Number(item.price ?? 0), 0);

        const previousAuctionRevenue = endedAuctions
            .filter((item) => {
                const updatedAt = new Date(item.updated_at);
                return updatedAt >= new Date(previousWindowIso) && updatedAt < new Date(currentWindowIso);
            })
            .reduce((sum, item) => sum + Number(item.current_bid ?? 0), 0);

        const currentSellTimes = [
            ...soldListings.map((item) => {
                const vehicleCreatedAt = item.vehicle?.created_at ?? item.created_at;
                return (new Date(item.updated_at).getTime() - new Date(vehicleCreatedAt).getTime()) / (1000 * 60 * 60 * 24);
            }),
            ...endedAuctions.map((item) => {
                const vehicleCreatedAt = item.vehicle?.created_at ?? item.created_at;
                return (new Date(item.ending).getTime() - new Date(vehicleCreatedAt).getTime()) / (1000 * 60 * 60 * 24);
            })
        ].filter((value) => Number.isFinite(value) && value >= 0);

        const previousSellTimes = [
            ...soldListings
                .filter((item) => {
                    const updatedAt = new Date(item.updated_at);
                    return updatedAt >= new Date(previousWindowIso) && updatedAt < new Date(currentWindowIso);
                })
                .map((item) => {
                    const vehicleCreatedAt = item.vehicle?.created_at ?? item.created_at;
                    return (new Date(item.updated_at).getTime() - new Date(vehicleCreatedAt).getTime()) / (1000 * 60 * 60 * 24);
                }),
            ...endedAuctions
                .filter((item) => {
                    const ending = new Date(item.ending);
                    return ending >= new Date(previousWindowIso) && ending < new Date(currentWindowIso);
                })
                .map((item) => {
                    const vehicleCreatedAt = item.vehicle?.created_at ?? item.created_at;
                    return (new Date(item.ending).getTime() - new Date(vehicleCreatedAt).getTime()) / (1000 * 60 * 60 * 24);
                })
        ].filter((value) => Number.isFinite(value) && value >= 0);

        const listingRevenue = soldListings.reduce((sum, item) => sum + Number(item.price ?? 0), 0);
        const auctionRevenue = endedAuctions.reduce((sum, item) => sum + Number(item.current_bid ?? 0), 0);
        const leadCounts = new Map<string, number>();
        const watchCounts = new Map<string, number>();

        events.forEach((event) => {
            const map = event.event_type === "lead" ? leadCounts : event.event_type === "watch" ? watchCounts : undefined;
            if (!map) return;
            map.set(event.vehicle_id, (map.get(event.vehicle_id) ?? 0) + 1);
        });

        const totalViews = vehicles.reduce((sum, item) => sum + Number(item.views ?? 0), 0);
        const totalLeads = Array.from(leadCounts.values()).reduce((sum, count) => sum + count, 0);
        const conversionRate = totalViews === 0 ? 0 : ((totalLeads / totalViews) * 100);

        const urgentQueue: UrgentItem[] = [
            ...auctions
                .map((item) => ({
                    ...item,
                    vehicle: normalizeVehicleRelation(item.vehicle)
                }))
                .filter((item) => item.status === "live")
                .sort((left, right) => new Date(left.ending).getTime() - new Date(right.ending).getTime())
                .slice(0, 3)
                .map((item) => {
                    const minutes = Math.max(1, Math.round((new Date(item.ending).getTime() - Date.now()) / (1000 * 60)));
                    return {
                        title: vehicleTitle(item.vehicle),
                        detail: `Auction closes in ${minutes} min`,
                        severity: minutes <= 60 ? "critical" as const : "warning" as const
                    };
                }),
            ...vehicles
                .filter((item) => item.active === "inactive" || item.published === false)
                .slice(0, 2)
                .map((item) => ({
                    title: vehicleTitle(item),
                    detail: "Inventory needs attention before it can perform",
                    severity: "warning" as const
                }))
        ].slice(0, 3);

        const topPerformers: TopPerformer[] = [
            ...normalizedListings.map((item) => ({
                name: vehicleTitle(item.vehicle),
                channel: "Listing" as const,
                sellThrough: Math.min(
                    100,
                    Math.max(
                        10,
                        leadCounts.get(String(item.vehicle_id)) && Number(item.vehicle?.views ?? 0) > 0
                            ? Math.round(((leadCounts.get(String(item.vehicle_id)) ?? 0) / Number(item.vehicle?.views ?? 0)) * 100)
                            : Number(item.vehicle?.views ?? 0)
                    )
                ),
                margin: Number(item.price ?? 0)
            })),
            ...normalizedAuctions.map((item) => ({
                name: vehicleTitle(item.vehicle),
                channel: "Auction" as const,
                sellThrough: Math.min(
                    100,
                    Math.max(
                        10,
                        watchCounts.get(String(item.vehicle_id)) && Number(item.vehicle?.views ?? 0) > 0
                            ? Math.round(((watchCounts.get(String(item.vehicle_id)) ?? 0) / Number(item.vehicle?.views ?? 0)) * 100)
                            : Number(item.vehicle?.views ?? 0)
                    )
                ),
                margin: Number(item.current_bid ?? item.starting_bid ?? 0)
            }))
        ]
            .sort((left, right) => right.sellThrough - left.sellThrough || right.margin - left.margin)
            .slice(0, 3);

        const bidBuckets = Array.from({length: 12}, () => 0);
        bids
            .filter((item) => new Date(item.created_at) >= new Date(todayStartIso))
            .forEach((item) => {
                const hour = new Date(item.created_at).getHours();
                if (hour < 8 || hour > 19) {
                    return;
                }

                const bucket = hour - 8;
                bidBuckets[bucket] += 1;
            });

        return {
            activeListings: {
                value: activeListings,
                ...percentageTrend(activeListings, previousActiveListings)
            },
            liveAuctions: {
                value: liveAuctions,
                ...percentageTrend(liveAuctions, previousLiveAuctions)
            },
            todayRevenue: {
                value: currentRevenue,
                ...percentageTrend(currentRevenue, previousListingRevenue + previousAuctionRevenue)
            },
            avgSellTime: {
                value: Number(average(currentSellTimes).toFixed(1)),
                ...dayTrend(Number(average(currentSellTimes).toFixed(1)), Number(average(previousSellTimes).toFixed(1)))
            },
            auctionRevenue,
            listingRevenue,
            conversionRate: Number(conversionRate.toFixed(1)),
            urgentQueue,
            topPerformers,
            hourlyBidHeat: bidBuckets
        };
    } catch (error) {
        return rejectWithValue(normalizeError(error, "Failed to fetch dashboard metrics"));
    }
});

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardMetrics.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
                state.loading = false;
                state.metrics = action.payload;
            })
            .addCase(fetchDashboardMetrics.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) ?? action.error.message ?? "Failed to fetch dashboard metrics";
            });
    }
});

export default dashboardSlice;
